import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";

// ACTA_ACTION_MENU_COMMON_V2

export function ActaActionMenu({
  open,
  anchorEl,
  onClose,
  children,
  width = 230,
  estimatedHeight = 220,
  testId = "acta-action-menu",
}) {
  const menuRef = useRef(null);
  const [position, setPosition] =
    useState(null);

  useLayoutEffect(() => {
    if (
      !open ||
      !anchorEl ||
      typeof window === "undefined"
    ) {
      setPosition(null);
      return;
    }

    const rect =
      anchorEl.getBoundingClientRect();

    const padding = 12;
    const gap = 8;

    const availableBelow =
      window.innerHeight -
      rect.bottom -
      padding;

    const availableAbove =
      rect.top -
      padding;

    const opensAbove =
      availableBelow < estimatedHeight &&
      availableAbove > availableBelow;

    const left = Math.min(
      window.innerWidth -
        width -
        padding,
      Math.max(
        padding,
        rect.right - width,
      ),
    );

    const requestedTop = opensAbove
      ? rect.top -
        estimatedHeight -
        gap
      : rect.bottom + gap;

    const top = Math.min(
      window.innerHeight -
        estimatedHeight -
        padding,
      Math.max(
        padding,
        requestedTop,
      ),
    );

    setPosition({
      left,
      top,
      opensAbove,
    });
  }, [
    anchorEl,
    estimatedHeight,
    open,
    width,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(
          event.target,
        ) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose?.();
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose?.();
      }
    }

    const handleViewportChange = () => {
      onClose?.();
    };

    document.addEventListener(
      "mousedown",
      handleOutside,
    );
    document.addEventListener(
      "keydown",
      handleKeyDown,
    );
    window.addEventListener(
      "resize",
      handleViewportChange,
    );
    window.addEventListener(
      "scroll",
      handleViewportChange,
      true,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutside,
      );
      document.removeEventListener(
        "keydown",
        handleKeyDown,
      );
      window.removeEventListener(
        "resize",
        handleViewportChange,
      );
      window.removeEventListener(
        "scroll",
        handleViewportChange,
        true,
      );
    };
  }, [
    anchorEl,
    onClose,
    open,
  ]);

  if (
    !open ||
    !position ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={`acta-action-menu ${
        position.opensAbove
          ? "opens-above"
          : "opens-below"
      }`}
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: `${width}px`,
      }}
      role="menu"
      data-acta-action-menu
      data-testid={testId}
      onClick={(event) =>
        event.stopPropagation()
      }
    >
      {children}
    </div>,
    document.body,
  );
}

export function ActaActionMenuItem({
  icon,
  label,
  description,
  danger = false,
  disabled = false,
  onClick,
  href,
  target,
  rel,
  testId,
}) {
  const className = [
    "acta-action-menu-item",
    danger ? "is-danger" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {icon}
      <span className="acta-action-menu-item-copy">
        <strong>{label}</strong>

        {description ? (
          <small>{description}</small>
        ) : null}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        role="menuitem"
        className={className}
        onClick={onClick}
        data-testid={testId}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      className={className}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      {content}
    </button>
  );
}

export function ActaActionSheet({
  open,
  title = "Actions",
  subtitle,
  onClose,
  children,
  testId = "acta-action-sheet",
}) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose?.();
      }
    }

    document.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    onClose,
    open,
  ]);

  if (
    !open ||
    typeof document === "undefined"
  ) {
    return null;
  }

  return createPortal(
    <div
      className="acta-action-sheet-backdrop"
      onClick={onClose}
      data-testid={`${testId}-backdrop`}
    >
      <section
        className="acta-action-sheet"
        role="dialog"
        aria-modal="true"
        data-acta-modal-kind="action-sheet"
        aria-label={title}
        data-acta-action-sheet
        data-testid={testId}
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        <header className="acta-action-sheet-header">
          <div>
            <strong>{title}</strong>

            {subtitle ? (
              <small>{subtitle}</small>
            ) : null}
          </div>

          <button
            type="button"
            className="acta-action-sheet-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </header>

        <div className="acta-action-sheet-content">
          {children}
        </div>
      </section>
    </div>,
    document.body,
  );
}
